import {
  forwardRef,
  useContext,
  MouseEvent,
  useRef,
  ComponentPropsWithoutRef,
  useEffect,
  useState,
  KeyboardEvent,
} from "react";
import { StepperContext } from "./Stepper";
import styles from "./StepperStep.module.scss";
import cx from "classnames";
import { Icon } from "../Icon";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import { useMergeRefs } from "../../hooks";

/**
 * Props for the StepperStep component
 * @extends ComponentPropsWithoutRef<"button">
 */
export type StepperStepProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick" | "disabled"
> & {
  /**
   * ID of the panel that this step is controlling.
   * Must match the id prop of the corresponding Stepper.Panel.
   */
  controls: string;

  /**
   * Called when the step is clicked.
   * @param e Mouse event object
   * @param index Index of the step in the stepper
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>, index?: number) => void;
};

/**
 * Individual step component for the Stepper.
 *
 * Features:
 * - Visual progress indicator with animated bar
 * - Check icon for completed steps
 * - Keyboard navigation support
 * - Accessible with proper ARIA attributes
 * - Click handling for step navigation
 * - Visual states for current, completed, and future steps
 * - Responsive design with mobile-friendly layout
 * - Focus management for keyboard users
 * - Custom styling support
 *
 * @example
 * <Stepper.Step controls="step1">
 *   Personal Information
 * </Stepper.Step>
 *
 * @example
 * <Stepper.Step
 *   controls="step2"
 *   onClick={(e, index) => console.log(`Clicked step ${index}`)}
 * >
 *   Contact Details
 * </Stepper.Step>
 */
export const StepperStep = forwardRef<HTMLButtonElement, StepperStepProps>(
  (props, ref) => {
    const stepRef = useRef<HTMLButtonElement>(null);
    const {
      current,
      items,
      setCurrent,
      focus,
      setFocus,
      allCompleted,
      allowNavigateToPrevStep,
    } = useContext(StepperContext);
    const {
      onClick,
      children,
      onFocus,
      onKeyDown,
      controls,
      className,
      ...rest
    } = props;

    const index = useRef<number>();
    const [selected, setSelected] = useState(false);
    const [completed, setCompleted] = useState(
      // Below is to be used for custom overriding
      // completed state for a step
      // Check Stepper.stories.tsx#103 for the use case
      stepRef.current?.getAttribute("data-completed") === "true"
        ? true
        : undefined,
    );

    useEffect(() => {
      if (!items) return;
      index.current = Array.prototype.indexOf.call(items, stepRef.current);

      setCompleted(index.current < current);
      setSelected(index.current === current);
    }, [current, items]);

    const stepClassNames = cx(styles["stepper-step"], className, {
      [styles["can-navigate"]]: allowNavigateToPrevStep,
      [styles["all-completed"]]: allCompleted,
    });

    const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e, index.current);
      if (
        !allowNavigateToPrevStep ||
        selected ||
        index.current === undefined ||
        !items ||
        index.current > current
      ) {
        return;
      }
      setCurrent(index.current ?? 0);
      setFocus(index.current);
      items[index.current].focus();
    };

    const onKeyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
      onKeyDown?.(e);
      const focusableItems = Array.prototype.filter.call(
        items,
        (item) =>
          item.getAttribute("data-completed") === "true" ||
          item.getAttribute("aria-selected") === "true",
      );
      const prevFocus = focus - 1;
      const nextFocus = focus + 1;
      switch (e.code) {
        case "Enter":
        case "Space":
          e.preventDefault();
          setCurrent(focus);
          break;
        case "ArrowRight":
          e.preventDefault();
          if (nextFocus === focusableItems.length) {
            setFocus(0);
            focusableItems[0].focus();
            focusableItems[0].setAttribute("tabindex", "0");
            stepRef.current?.setAttribute("tabindex", "-1");
            return;
          }
          stepRef.current?.setAttribute("tabindex", "-1");
          setFocus(nextFocus);
          focusableItems[nextFocus].focus();
          focusableItems[nextFocus].setAttribute("tabindex", "0");
          break;
        case "ArrowLeft":
          e.preventDefault();
          if (focus === 0) {
            const lastItemIndex = focusableItems.length - 1;
            setFocus(lastItemIndex);
            focusableItems[lastItemIndex].focus();
            focusableItems[lastItemIndex].setAttribute("tabindex", "0");
            stepRef.current?.setAttribute("tabindex", "-1");
            return;
          }
          stepRef.current?.setAttribute("tabindex", "-1");
          setFocus(prevFocus);
          focusableItems[prevFocus].focus();
          focusableItems[prevFocus].setAttribute("tabindex", "0");
          break;
      }
    };

    return (
      <button
        type="button"
        role="tab"
        data-anv="stepper-step"
        className={stepClassNames}
        aria-selected={selected}
        aria-controls={controls}
        tabIndex={allowNavigateToPrevStep && selected ? 0 : -1}
        data-completed={completed}
        onClick={onClickHandler}
        onKeyDown={allowNavigateToPrevStep ? onKeyDownHandler : onKeyDown}
        {...rest}
        ref={useMergeRefs([stepRef, ref])}
      >
        <span className={styles.bar} />
        <span className={styles.label}>
          <span className={styles.circle}>
            <Icon svg={Check} inherit aria-hidden />
          </span>

          {children}
        </span>
      </button>
    );
  },
);

StepperStep.displayName = "StepperStep";
